// ----------------------------------------------------------------
// <copyright company='Microsoft Corporation'>
//     Copyright (c) Microsoft Corporation.  All rights reserved.
// </copyright>
// ----------------------------------------------------------------

import { all, call, put, select } from 'redux-saga/effects';

import history from 'src/history';
import DeviceDal from './../../../dal/DeviceDal';
import { logException } from './../../../lib/ApplicationInsights';
import CorrelationVector from './../../../lib/CorrelationVector';
import {
    actionStatusStarted,
    actionStatusFailed,
    actionStatusSuccess,
} from './../../../store/actionStatus';
import { saveZipCodeData } from './../../../store/orderCreation';

import { getDeviceEligibilityData } from './../../../store/deviceInfo/eligibility';
import { getBatchServiceRequestData } from './../../../store/deviceInfo/claimOrder';
import {
    GET_DEVICE_INFO_DATA,
    getDeviceInfoDataFail,
    getDeviceInfoDataSuccess,
} from './../../../store/deviceInfo/deviceInfo';
import { isExternalTier } from './../../../lib/userTierConfig';
import { userAgentTierSelector } from './../../../store/user/selector';

export function* fetchDeviceInfo(request: any): IterableIterator<any> {
    const {
        country,
        noReroute,
        serialNumber,
        zipCode,
        noBatchCall,
        isWarrantyOnly,
    } = request.payload;

    try {
        const agentTier = yield select(state => userAgentTierSelector(state));

        yield put(actionStatusStarted({ actionName: GET_DEVICE_INFO_DATA }));

        const deviceInfo: any = yield call(DeviceDal.getDevice, serialNumber);

        yield put(getDeviceInfoDataSuccess(deviceInfo));

        yield put(actionStatusSuccess({ actionName: GET_DEVICE_INFO_DATA }));

        if (!isWarrantyOnly) {
            yield* getAdditionalDeviceInfo(deviceInfo, agentTier);
        }
    } catch (e) {
        yield put(
            actionStatusFailed({
                actionName: GET_DEVICE_INFO_DATA,
                data: { serialNumber },
                error: e,
            })
        );
        yield put(getDeviceInfoDataFail(e));
    }

    function getOrderList(allTheOrder: any[], tierAgent: any, customerId: string, passportId: string) {
        if (isExternalTier(tierAgent)) {
            return allTheOrder.filter((claim: any) => claim.sceProfileId === customerId || (claim.passportId ? claim.passportId === passportId : null));
        }
        return allTheOrder;
    }

    function* getAdditionalDeviceInfo(deviceInfo: any, agentTier: undefined) {
        const { claims, registration } = deviceInfo;
        const { customerId, passportId } = registration || {};

        const orderList = getOrderList(claims, agentTier, customerId, passportId);
        const serviceRequestIds: any[] = [
            ...new Set(
                ((orderList) || []).map(
                    (claim: any) => claim.claimOrderId
                )
            ),
        ];

        // Ensure all the claims have claimOrderId, otherwise raise an alert
        const validServiceRequestIds = serviceRequestIds.filter(
            (claimOrderId: string) => !!claimOrderId
        );
        if (validServiceRequestIds.length !== serviceRequestIds.length) {
            logException(
                CorrelationVector.getCurrentValue(),
                new Error(
                    `Device with SN ${serialNumber} has one or more invalid claims`
                ),
                {
                    serialNumber,
                    claims: deviceInfo.claims,
                }
            );
        }
        yield all([
            put(
                getDeviceEligibilityData({
                    serialNumber,
                    country,
                    zipCode,
                    agentTier,
                })
            ),
            !noBatchCall &&
            put(getBatchServiceRequestData(validServiceRequestIds)),
        ]);
        if (zipCode) {
            yield put(saveZipCodeData({ zipCode }));
        }
        if (!noReroute) history.push('/device');
    }
}
